import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface FlightOffer {
  id: string;
  airline: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  stops: number;
  price: number;
  currency: string;
  cabinClass: string;
  amenities?: string[];
}

interface UseFlightSearchReturn {
  results: FlightOffer[];
  search: (params: {
    originLocationCode: string;
    destinationLocationCode: string;
    departureDate: string;
    returnDate?: string;
    adults?: number;
    travelClass?: string;
    nonStop?: boolean;
    maxPrice?: number;
    currencyCode?: string;
  }) => Promise<void>;
  loading: boolean;
  error: string | null;
  clearResults: () => void;
  clearError: () => void;
}

export function useFlightSearch(): UseFlightSearchReturn {
  const [results, setResults] = useState<FlightOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (params: {
    originLocationCode: string;
    destinationLocationCode: string;
    departureDate: string;
    returnDate?: string;
    adults?: number;
    travelClass?: string;
    nonStop?: boolean;
    maxPrice?: number;
    currencyCode?: string;
  }): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/search-flights`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify(params),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Flights API error:', response.status, errorText);
        throw new Error(errorText || `Failed to search flights (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('Flights API response:', data);

      if (data.error) {
        console.error('Flights API error message:', data.error);
        setError(data.message || data.error);
        setResults([]);
        return;
      }

      const flights = data.flights || [];
      setResults(flights);

      if (flights.length === 0) {
        if (!data.success) {
          setError(data.message || 'No flights found. Try different dates or routes.');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search flights';
      console.error('Flights search error:', err);
      setError(message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    results,
    search,
    loading,
    error,
    clearResults,
    clearError,
  };
}
